import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { Company } from '../types';
import { colors } from '../styles/colors';
import { maskCNPJ, maskCEP, maskPhone } from '../utils/formatters';

interface CompanyFormProps {
  company?: Company & { addresses?: any[]; slug?: { name: string } };
  onSave: () => void;
  onCancel: () => void;
}

const CompanyForm: React.FC<CompanyFormProps> = ({ company, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    fancy_name: '',
    url: '',
    status: 0,
    description: '',
    employees: '',
    cnpj: '',
    email: '',
    segment: '',
    startup: false,
    pto_contato: false,
    linkedin_id: '',
    is_eoe: false,
    foreign_company: false,
    linkedin_share_jobs: false,
    gender: ''
  });
  const [slugData, setSlugData] = useState({ name: '' });
  const [address, setAddress] = useState<any>({});
  const [addresses, setAddresses] = useState<any[]>([]);
  const [network, setNetwork] = useState<any>({});
  const [networks, setNetworks] = useState<any[]>([]);
  const [availableNetworks, setAvailableNetworks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    fetchAvailableNetworks();
    if (company?.id) {
      fetchCompanyData(company.id);
    } else if (company) {
      setFormData({
        name: company.name || '',
        fancy_name: company.fancy_name || '',
        url: company.url || '',
        status: company.status || 0,
        description: company.description || '',
        employees: company.employees || '',
        cnpj: company.cnpj || '',
        email: company.email || '',
        segment: company.segment || '',
        startup: company.startup || false,
        pto_contato: company.pto_contato || false,
        linkedin_id: company.linkedin_id || '',
        is_eoe: company.is_eoe || false,
        foreign_company: company.foreign_company || false,
        linkedin_share_jobs: company.linkedin_share_jobs || false,
        gender: company.gender || ''
      });
      if (company.slug) {
        setSlugData({ name: company.slug.name || '' });
      }
    }
  }, [company]);

  const fetchAvailableNetworks = async () => {
    try {
      const response = await api.get('/networks');
      setAvailableNetworks(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar redes:', error);
    }
  };

  const fetchCompanyData = async (companyId: number) => {
    try {
      setLoadingData(true);
      const response = await api.get(`/companies/${companyId}`);
      const data = response.data;
      
      setFormData({
        name: data.name || '',
        fancy_name: data.fancy_name || '',
        url: data.url || '',
        status: data.status || 0,
        description: data.description || '',
        employees: data.employees || '',
        cnpj: data.cnpj || '',
        email: data.email || '',
        segment: data.segment || '',
        startup: data.startup || false,
        pto_contato: data.pto_contato || false,
        linkedin_id: data.linkedin_id || '',
        is_eoe: data.is_eoe || false,
        foreign_company: data.foreign_company || false,
        linkedin_share_jobs: data.linkedin_share_jobs || false,
        gender: data.gender || ''
      });
      if (data.slug) {
        setSlugData({ name: data.slug.name || '' });
      }
      setAddresses(data.addresses || []);
      setNetworks(data.networks || []);
      setAddress({});
      setNetwork({});
    } catch (error: any) {
      toast.error('Erro ao carregar dados da empresa');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = { 
        ...formData, 
        cnpj: formData.foreign_company ? '' : formData.cnpj.replace(/\D/g, ''), // Remove máscara do CNPJ apenas se não for estrangeira
        addresses: addresses.map(addr => ({
          ...addr,
          zip: addr.zip ? addr.zip.replace(/\D/g, '') : '',
          telephone: addr.telephone ? addr.telephone.replace(/\D/g, '') : ''
        })),
        networks: networks,
        slug_attributes: slugData
      };
      
      if (company) {
        await api.put(`/companies/${company.id}`, data);
        toast.success('Empresa atualizada com sucesso!');
      } else {
        await api.post('/companies', data);
        toast.success('Empresa criada com sucesso!');
      }
      
      onSave();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addNetwork = () => {
    if (network.network_id && network.username) {
      const selectedNetwork = availableNetworks.find(n => n.id == network.network_id);
      setNetworks(prev => [...prev, { 
        ...network, 
        network_name: selectedNetwork?.name || 'Rede desconhecida'
      }]);
      setNetwork({});
    }
  };

  const removeNetwork = (index: number) => {
    setNetworks(prev => prev.filter((_, i) => i !== index));
  };

  const updateNetwork = (field: string, value: string) => {
    setNetwork((prev: any) => ({ ...prev, [field]: value }));
  };

  const clearNetwork = () => {
    setNetwork({});
  };

  const addAddress = () => {
    if (Object.values(address).some(v => v && v.toString().trim())) {
      setAddresses(prev => [...prev, { ...address }]);
      setAddress({});
    }
  };

  const removeAddress = (index: number) => {
    setAddresses(prev => prev.filter((_, i) => i !== index));
  };

  const updateAddress = (field: string, value: string) => {
    setAddress((prev: any) => ({ ...prev, [field]: value }));
  };

  const clearAddress = () => {
    setAddress({});
  };

  if (loadingData) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.loading}>Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            {company ? 'Editar Empresa' : 'Nova Empresa'}
          </h2>
          <button onClick={onCancel} style={styles.closeBtn} type="button">×</button>
        </div>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.sectionTitle}>Dados Básicos</h3>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Nome *:</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Slug (URL amigável):</label>
            <input
              type="text"
              value={slugData.name}
              onChange={(e) => setSlugData({ name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') })}
              style={styles.input}
              placeholder="nome-da-empresa"
            />
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.foreign_company}
                onChange={(e) => handleChange('foreign_company', e.target.checked)}
              />
              Empresa estrangeira? (desabilita a validação de CNPJ)
            </label>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>CNPJ:</label>
            <input
              type="text"
              value={formData.cnpj}
              onChange={(e) => handleChange('cnpj', maskCNPJ(e.target.value))}
              style={styles.input}
              maxLength={18}
              disabled={formData.foreign_company}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email:</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Ativa?</label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', parseInt(e.target.value))}
              style={styles.input}
            >
              <option value={0}>Inativo</option>
              <option value={1}>Ativo</option>
            </select>
          </div>

          <h3 style={styles.sectionTitle}>Mais detalhes (campos opcionais)</h3>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Nome Fantasia:</label>
            <input
              type="text"
              value={formData.fancy_name}
              onChange={(e) => handleChange('fancy_name', e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Gênero:</label>
            <select
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              style={styles.input}
            >
              <option value="">Selecione</option>
              <option value="masculino">Masculino</option>
              <option value="feminino">Feminino</option>
              <option value="neutro">Neutro</option>
            </select>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Tamanho da Empresa:</label>
            <select
              value={formData.employees}
              onChange={(e) => handleChange('employees', e.target.value)}
              style={styles.input}
            >
              <option value="">Selecione o tamanho</option>
              <option value="Somente eu">Somente eu</option>
              <option value="2-10 funcionários">2-10 funcionários</option>
              <option value="11-50 funcionários">11-50 funcionários</option>
              <option value="51-100 funcionários">51-100 funcionários</option>
              <option value="101-200 funcionários">101-200 funcionários</option>
              <option value="201-500 funcionários">201-500 funcionários</option>
              <option value="501-1000 funcionários">501-1000 funcionários</option>
              <option value="mais de 1000 funcionários">mais de 1000 funcionários</option>
            </select>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Descrição:</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              style={{...styles.input, minHeight: '100px'}}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>URL:</label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => handleChange('url', e.target.value)}
              style={styles.input}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Segmento:</label>
            <select
              value={formData.segment}
              onChange={(e) => handleChange('segment', e.target.value)}
              style={styles.input}
            >
              <option value=""></option>
              <option value="Agência de Marketing e Publicidade">Agência de Marketing e Publicidade</option>
              <option value="Agronegócio">Agronegócio</option>
              <option value="Consultorias e Treinamentos">Consultorias e Treinamentos</option>
              <option value="E-commerce">E-commerce</option>
              <option value="Educação e Ensino">Educação e Ensino</option>
              <option value="Engenharia e Indústria Geral">Engenharia e Indústria Geral</option>
              <option value="Estúdio ou Produtora Audiovisual">Estúdio ou Produtora Audiovisual</option>
              <option value="Eventos">Eventos</option>
              <option value="Financeiro, Jurídico e Serviços Relacionados">Financeiro, Jurídico e Serviços Relacionados</option>
              <option value="Governo e Órgãos Públicos">Governo e Órgãos Públicos</option>
              <option value="Hardware e Eletrônicos">Hardware e Eletrônicos</option>
              <option value="Imobiliárias">Imobiliárias</option>
              <option value="Mídia e Comunicação">Mídia e Comunicação</option>
              <option value="ONGs">ONGs</option>
              <option value="Saúde e Estética">Saúde e Estética</option>
              <option value="Serviços em Geral">Serviços em Geral</option>
              <option value="Serviços em RH e Coaching">Serviços em RH e Coaching</option>
              <option value="Software e Cloud">Software e Cloud</option>
              <option value="Telecomunicações">Telecomunicações</option>
              <option value="Turismo e Lazer">Turismo e Lazer</option>
              <option value="Varejo">Varejo</option>
              <option value="Veículo de Comunicação">Veículo de Comunicação</option>
            </select>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.startup}
                onChange={(e) => handleChange('startup', e.target.checked)}
              />
              Startup?
            </label>
          </div>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.is_eoe}
                onChange={(e) => handleChange('is_eoe', e.target.checked)}
              />
              Apoia a diversidade e inclusão?
            </label>
          </div>

          <h3 style={styles.sectionTitle}>LinkedIn</h3>

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.linkedin_share_jobs}
                onChange={(e) => handleChange('linkedin_share_jobs', e.target.checked)}
              />
              Compartilhar oportunidades no LinkedIn?
            </label>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>ID da Empresa:</label>
            <input
              type="text"
              value={formData.linkedin_id}
              onChange={(e) => handleChange('linkedin_id', e.target.value)}
              style={styles.input}
              placeholder="ID da empresa no LinkedIn"
            />
          </div>

          <h3 style={styles.sectionTitle}>Redes Sociais</h3>

          {networks.length > 0 && (
            <div style={styles.addressesList}>
              {networks.map((net, index) => {
                const networkInfo = net.network_name ? net : availableNetworks.find(n => n.id === net.network_id);
                return (
                  <div key={index} style={styles.addressItem}>
                    <div style={styles.addressInfo}>
                      <strong>{net.network_name || networkInfo?.name || 'Rede desconhecida'}</strong>
                      <br />
                      Username: {net.username}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeNetwork(index)}
                      style={styles.removeBtn}
                    >
                      Remover
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Adicionar nova rede social:</label>
            <div style={styles.addressBlock}>
              <select
                value={network.network_id || ''}
                onChange={(e) => updateNetwork('network_id', e.target.value)}
                style={{...styles.input, marginBottom: '0.5rem'}}
              >
                <option value="">Selecione uma rede</option>
                {availableNetworks.map(net => (
                  <option key={net.id} value={net.id}>{net.name}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Username"
                value={network.username || ''}
                onChange={(e) => updateNetwork('username', e.target.value)}
                style={{...styles.input, marginBottom: '0.5rem'}}
              />
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                {network.network_id && network.username && (
                  <button
                    type="button"
                    onClick={clearNetwork}
                    style={styles.removeBtn}
                  >
                    Limpar
                  </button>
                )}
                <button
                  type="button"
                  onClick={addNetwork}
                  style={{...styles.addBtn, marginLeft: 'auto'}}
                  disabled={!network.network_id || !network.username}
                >
                  Adicionar Rede
                </button>
              </div>
            </div>
          </div>

          <h3 style={styles.sectionTitle}>Endereços</h3>

          {addresses.length > 0 && (
            <div style={styles.addressesList}>
              {addresses.map((addr, index) => (
                <div key={index} style={styles.addressItem}>
                  <div style={styles.addressInfo}>
                    <strong>{addr.street || 'Sem rua'}, {addr.number || 's/n'}</strong>
                    {addr.complement && <span> - {addr.complement}</span>}
                    <br />
                    {addr.neighborhood && <span>{addr.neighborhood}, </span>}
                    {addr.city || 'Sem cidade'} - {addr.state || 'Sem estado'}
                    {addr.zip && <span> - CEP: {addr.zip}</span>}
                    {addr.telephone && <span> - Tel: {addr.telephone}</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAddress(index)}
                    style={styles.removeBtn}
                  >
                    Remover
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Adicionar novo endereço:</label>
            <div style={styles.addressBlock}>
              <input
                type="text"
                placeholder="Rua"
                value={address.street || ''}
                onChange={(e) => updateAddress('street', e.target.value)}
                style={{...styles.input, marginBottom: '0.5rem'}}
              />
              <div style={{display: 'flex', gap: '0.5rem', marginBottom: '0.5rem'}}>
                <input
                  type="text"
                  placeholder="Número"
                  value={address.number || ''}
                  onChange={(e) => updateAddress('number', e.target.value)}
                  style={{...styles.input, flex: 1}}
                />
                <input
                  type="text"
                  placeholder="Complemento"
                  value={address.complement || ''}
                  onChange={(e) => updateAddress('complement', e.target.value)}
                  style={{...styles.input, flex: 1}}
                />
              </div>
              <div style={{display: 'flex', gap: '0.5rem', marginBottom: '0.5rem'}}>
                <input
                  type="text"
                  placeholder="Bairro"
                  value={address.neighborhood || ''}
                  onChange={(e) => updateAddress('neighborhood', e.target.value)}
                  style={{...styles.input, flex: 1}}
                />
                <input
                  type="text"
                  placeholder="CEP"
                  value={address.zip || ''}
                  onChange={(e) => updateAddress('zip', maskCEP(e.target.value))}
                  style={{...styles.input, flex: 1}}
                  maxLength={9}
                />
              </div>
              <div style={{display: 'flex', gap: '0.5rem', marginBottom: '0.5rem'}}>
                <input
                  type="text"
                  placeholder="Cidade"
                  value={address.city || ''}
                  onChange={(e) => updateAddress('city', e.target.value)}
                  style={{...styles.input, flex: 1}}
                />
                <input
                  type="text"
                  placeholder="Estado"
                  value={address.state || ''}
                  onChange={(e) => updateAddress('state', e.target.value)}
                  style={{...styles.input, flex: 1}}
                />
              </div>
              <div style={{display: 'flex', gap: '0.5rem', marginBottom: '0.5rem'}}>
                <input
                  type="text"
                  placeholder="País"
                  value={address.country || ''}
                  onChange={(e) => updateAddress('country', e.target.value)}
                  style={{...styles.input, flex: 1}}
                />
                <input
                  type="tel"
                  placeholder="Telefone"
                  value={address.telephone || ''}
                  onChange={(e) => updateAddress('telephone', maskPhone(e.target.value))}
                  style={{...styles.input, flex: 1}}
                  maxLength={15}
                />
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                {Object.values(address).some(v => v && v.toString().trim()) && (
                  <button
                    type="button"
                    onClick={clearAddress}
                    style={styles.removeBtn}
                  >
                    Limpar
                  </button>
                )}
                <button
                  type="button"
                  onClick={addAddress}
                  style={{...styles.addBtn, marginLeft: 'auto'}}
                  disabled={!Object.values(address).some(v => v && v.toString().trim())}
                >
                  Adicionar Endereço
                </button>
              </div>
            </div>
          </div>
          
          <div style={styles.buttons}>
            <button
              type="button"
              onClick={onCancel}
              style={styles.cancelBtn}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={styles.saveBtn}
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem 2rem 0 2rem',
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
    padding: 0,
    lineHeight: 1,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '0 2rem 2rem 2rem',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: colors.primary,
    marginTop: '1.5rem',
    marginBottom: '1rem',
    borderBottom: `2px solid ${colors.primary}`,
    paddingBottom: '0.5rem',
  },
  inputGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    color: colors.text,
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  },
  checkboxGroup: {
    marginBottom: '1rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: colors.text,
    cursor: 'pointer',
  },
  buttons: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1rem',
  },
  cancelBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  saveBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  addressBlock: {
    padding: '1rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    marginBottom: '1rem',
    backgroundColor: '#f9f9f9',
  },
  addBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  removeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  addressesList: {
    marginBottom: '1rem',
  },
  addressItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    marginBottom: '0.5rem',
    backgroundColor: colors.light,
  },
  addressInfo: {
    flex: 1,
    fontSize: '0.9rem',
    lineHeight: '1.4',
  },
};

export default CompanyForm;
